import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Camera, X } from 'lucide-react';
import { supabase } from '../admin/lib/supabaseClient';
import { compressImage, uploadFile } from '../admin/utils/storage';
import CATEGORY_TREE from '../data/categories';
import toast from 'react-hot-toast';

type IssueOption = { label: string; value: string };

function flattenCategories(tree: any[]): IssueOption[] {
  const out: IssueOption[] = [];
  tree.forEach((n) => {
    if (n.children && n.children.length) {
      n.children.forEach((c: any) => out.push({ label: `${n.label} - ${c.label}`, value: `${n.value}::${c.value}` }));
    } else {
      out.push({ label: n.label, value: n.value });
    }
  });
  return out;
}

const ISSUE_OPTIONS = flattenCategories(CATEGORY_TREE);

export default function QuickQuote() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [issue, setIssue] = useState<string>('panel');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [sending, setSending] = useState(false);

  // Don't show on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const handleFiles = (fList: FileList | null) => {
    if (!fList) return;
    setFiles((s) => [...s, ...Array.from(fList)]);
  };

  const submitQuote = async () => {
    setSending(true);
    try {
      const uploaded: string[] = [];
      for (const f of files) {
        const compressed = await compressImage(f, 1);
        const url = await uploadFile('contact', compressed as File);
        uploaded.push(url);
      }

      const issues = [issue];
      const payload: any = {
        name: name || 'Unknown',
        email: email || null,
        phone: phone || null,
        message: notes || `Quick quote for ${issue}`,
        subject: 'quick_quote',
        source: 'quick_quote_widget',
        status: 'new',
        images: uploaded.length ? uploaded : undefined
      };

      // Try to add new fields, but don't fail if they don't exist yet
      try {
        payload.issues = issues;
        payload.quote_requested_at = new Date().toISOString();
      } catch (err) {
        console.warn('Database may not have latest schema - continuing without new fields');
      }

      const { error } = await supabase.from('contact_submissions').insert([payload]);
      if (error) throw error;
      toast.success('Quote request submitted — we will be in touch');
      setName(''); setEmail(''); setPhone(''); setNotes(''); setFiles([]); setOpen(false);
    } catch (err) {
      toast.error('Failed to submit quote request');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating tab */}
      <div className="fixed left-6 bottom-6 z-50">
        <motion.button
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          whileHover={{ scale: 1.03 }}
          onClick={() => setOpen(true)}
          className="bg-mini-red text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-3"
          aria-label="Get quick quote"
        >
          <Camera size={18} />
          <span className="hidden sm:inline">Quick Quote</span>
        </motion.button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Upload a photo for a quick quote</h3>
              <button onClick={() => setOpen(false)} aria-label="Close"><X /></button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 border rounded" />
              <input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="px-3 py-2 border rounded" />
              <input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="px-3 py-2 border rounded" />

              <label className="text-sm font-medium">Issue</label>
              <select value={issue} onChange={(e) => setIssue(e.target.value)} className="px-3 py-2 border rounded">
                {ISSUE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <label className="text-sm font-medium">Details (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="px-3 py-2 border rounded" />

              <div className="flex items-center gap-2">
                <input ref={inputRef} type="file" accept="image/*" onChange={(e) => handleFiles(e.target.files)} className="hidden" />
                <button onClick={() => inputRef.current?.click()} className="px-3 py-2 bg-gray-100 rounded">Attach Photo</button>
                <div className="flex gap-2">
                  {files.map((f, i) => (
                    <img key={i} src={URL.createObjectURL(f)} className="w-14 h-14 object-cover rounded" alt={f.name} />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setOpen(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button onClick={submitQuote} disabled={sending} className="px-4 py-2 bg-mini-red text-white rounded flex items-center gap-2">
                  <Send />
                  {sending ? 'Sending...' : 'Request Quote'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
